import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate } from 'react-router'

import {
  LOCALE_COOKIE_MAX_AGE_SECONDS,
  LOCALE_COOKIE_NAME,
  SHORT_LOCALES,
  parseShortLocale,
  shortToFull,
} from './locales.constants'
import { resolveLocaleFromBrowser } from './utils/locale-resolve'
import { emitLocaleChange, type LocaleChangeSource } from './utils/locale-telemetry'

interface LocaleContextValue {
  locale: SHORT_LOCALES
  // `source` defaults to 'switcher' because that's the only external caller
  // (LanguageSwitcher). The URL-hint useEffect inside this provider passes
  // 'url-hint' explicitly so funnel analysis can split the two.
  setLocale: (next: SHORT_LOCALES, source?: LocaleChangeSource) => void
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

// Always Path=/ — never write path-scoped variants. A more-specific Path entry
// would sort ahead of this one in document.cookie and silently outvote it on
// the next read (readCookieLocale takes the first match).
// Exported so the Domain/Secure derivation branches (which only fire on
// HTTPS paretos.com hosts) can be unit-tested in jsdom without faking a
// LocaleProvider mount under those hosts.
export const writeLocaleCookie = (value: SHORT_LOCALES): void => {
  if (typeof document === 'undefined') {
    return
  }
  const parts = [
    `${LOCALE_COOKIE_NAME}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${LOCALE_COOKIE_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ]
  // Scope to the per-env parent so the cookie crosses auth.* ↔ app.* WITHIN
  // an env, but DEV/STAGING/prod stay isolated. A blanket `Domain=.paretos.com`
  // leaks dev test data onto prod sessions (and vice-versa) for anyone using
  // the same browser profile against both. Derive the parent from the host:
  //   auth.paretos.com           → .paretos.com
  //   auth.dev.paretos.com       → .dev.paretos.com
  //   auth.staging.paretos.com   → .staging.paretos.com
  // Strict regex anchor so `auth.evilparetos.com` (no real `.paretos.com`
  // suffix) doesn't match. Host-only fallback for localhost/IP.
  const host = window.location.hostname
  const envMatch = host.match(/^auth\.((?:[a-z0-9-]+\.)?paretos\.com)$/)
  if (envMatch) {
    parts.push(`Domain=.${envMatch[1]}`)
  }
  if (window.location.protocol === 'https:') {
    parts.push('Secure')
  }
  document.cookie = parts.join('; ')
}

interface Props {
  children: ReactNode
  // Test seam: lets tests inject a deterministic locale without touching globals.
  initialLocale?: SHORT_LOCALES
}

export const LocaleProvider = ({ children, initialLocale }: Props) => {
  const [locale, setLocaleState] = useState<SHORT_LOCALES>(
    () => initialLocale ?? resolveLocaleFromBrowser(),
  )
  const location = useLocation()
  const navigate = useNavigate()

  // Reflect the resolved locale onto <html lang> so screen readers / spellcheck
  // pick the right language without authors having to remember. Use the full
  // BCP-47 tag (en-US / de-DE) — short tags are valid but less specific.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = shortToFull(locale)
    }
  }, [locale])

  // Mirror the latest `locale` into a ref so setLocale's diff/telemetry/cookie
  // side-effects can run OUTSIDE the state updater. React strongly recommends
  // updater functions stay pure; React 19's stricter double-invocation under
  // transitions would otherwise emit telemetry twice per click. Sync via
  // useEffect (concurrent-safe) rather than write during render.
  const localeRef = useRef(locale)
  useEffect(() => {
    localeRef.current = locale
  }, [locale])

  const setLocale = useCallback((next: SHORT_LOCALES, source: LocaleChangeSource = 'switcher') => {
    const current = localeRef.current
    // No-op when nothing actually changes — avoids polluting telemetry with
    // idle re-clicks on the active button and skips a redundant cookie write.
    if (current === next) {
      return
    }
    emitLocaleChange({ from: current, to: next, source })
    writeLocaleCookie(next)
    setLocaleState(next)
  }, [])

  // React to in-app navigation that flips `?lang=` mid-session — e.g. a Slack
  // link `auth.paretos.com/login?lang=de` arriving in a tab whose cookie says
  // `en`. Persist the change to the cookie so it sticks the same way a
  // <LanguageSwitcher/> click does: ignoring the URL hint and silently keeping
  // the old locale would surprise the user more than honouring it.
  //
  // Self-correcting strip: after applying the hint we replace the URL with a
  // `lang`-less variant. Without this, the effect's `locale` dep would fire
  // again on the next switcher click, see the still-present `?lang=de`, and
  // overwrite the click — visible bug. Stripping makes the URL clean for
  // sharing too.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const hint = parseShortLocale(params.get('lang'))
    if (!hint) {
      return
    }
    if (hint !== locale) {
      setLocale(hint, 'url-hint')
    }
    params.delete('lang')
    const cleanSearch = params.toString()
    navigate(
      {
        pathname: location.pathname,
        search: cleanSearch ? `?${cleanSearch}` : '',
        hash: location.hash,
      },
      { replace: true },
    )
  }, [location.pathname, location.search, location.hash, locale, setLocale, navigate])

  const value = useMemo<LocaleContextValue>(() => ({ locale, setLocale }), [locale, setLocale])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export const useLocale = (): LocaleContextValue => {
  const ctx = useContext(LocaleContext)
  if (!ctx) {
    throw new Error('useLocale must be used inside <LocaleProvider>')
  }
  return ctx
}
